class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedMap = new Map();
    this.focusElement = this.input;
    this.resetButton.addEventListener('click', this.clear.bind(this));
    this.input.addEventListener('input', FoxTheme.utils.debounce(this.onChange.bind(this), 300));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.searchContent = this.querySelector('.search__content');
    this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';
    this.header = document.querySelector('header');
    this.toggleNavigationButton = document.querySelector('.toggle-navigation-button');

    this.boundHandleClickOutside = this.handleClickOutside.bind(this);
    document.addEventListener('click', this.boundHandleClickOutside);
    this.searchProductTypes?.addEventListener('change', this.handleProductTypeChange.bind(this));

    this.isHeaderSearch = this.closest('header') !== null;

    document.addEventListener('menu-drawer:open', () => {
      this.classList.remove('predictive-search-open');
      document.body.classList.remove('search-open');
    });

    this.states = {
      OPEN: 'predictive-search-open',
      LOADING: 'btn--loading',
      SEARCH_OPEN: 'search-open',
    };
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }
  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }

  get searchProductTypes() {
    return this.querySelector('#SearchProductTypes');
  }

  get strictMatch() {
    return this.dataset.strictMatch !== 'false';
  }

  get displayLimit() {
    return parseInt(this.dataset.resultsLimit, 10) || 3;
  }

  get fetchLimit() {
    // Over-fetch so strict matching has spare candidates to refill the grid with.
    // 10 is the ceiling /search/suggest accepts for resources[limit].
    return Math.min(this.displayLimit * 2, 10);
  }

  static words(text) {
    return text
      .toLowerCase()
      .split(/[^a-z0-9]+/)
      .filter(Boolean);
  }

  /**
   * Shopify's /search/suggest scores loosely, has no all-terms-must-match mode, and
   * ranks by its own popularity signals. For "iphone 16" that means iPhone 14 appears
   * (because "iphone" alone matches and the endpoint pads out to resources[limit]),
   * while the plain "iPhone 16" ranks below "iPhone 16 Pro Max". We fix both here.
   *
   * Filter: every typed word must prefix-match a word in the card's title or brand.
   * Prefix, not equality, so results still narrow correctly mid-word. Brand matters
   * because the store has only four MacBooks, so Shopify pads a "macbook" search with
   * iPads - which title matching correctly drops. But a bare "apple" appears in almost
   * no title, and dropping every iPhone for it would be far worse, so brand rescues
   * that case: every Apple product matches, nothing is filtered.
   *
   * Rank: by how many title words the query never accounted for. "iphone 16" leaves
   * one leftover for "iPhone 16 - 128GB" but three for "iPhone 16 Pro Max - 256GB",
   * so the exact model sorts above its variants. Shopify's own order breaks ties, and
   * ranking happens before applyResultLimits trims, so the closest matches survive.
   * Brand is excluded from the leftover count so it cannot skew that ordering.
   *
   * Ranking is skipped for single-word queries: a broad "iphone" is better served by
   * Shopify's popularity order than by whichever model has the shortest name. The
   * filter still runs, which is what keeps iPads out of a "macbook" search.
   *
   * One escape hatch: if the filter would empty the grid, Shopify's list is kept
   * untouched. A typo ("ipone 16") or a category word that appears in no title at all
   * ("laptop") degrades to loose results rather than an empty dropdown.
   */
  filterAndRankProducts(contentElement) {
    const terms = PredictiveSearch.words(this.getQuery());
    if (terms.length === 0) return;

    const list = contentElement.querySelector('#predictive-search-results-products-list');
    if (!list) return;

    const scored = Array.from(list.querySelectorAll('.product-column')).map((card, index) => {
      const titleWords = PredictiveSearch.words(card.querySelector('.product-card__title')?.textContent || '');
      const matchWords = titleWords.concat(PredictiveSearch.words(card.dataset.searchBrand || ''));
      const accounted = (word) => terms.some((term) => word.startsWith(term));
      return {
        card,
        index,
        matchesAll: terms.every((term) => matchWords.some((word) => word.startsWith(term))),
        leftover: titleWords.filter((word) => !accounted(word)).length,
      };
    });

    const matched = scored.filter((entry) => entry.matchesAll);
    if (matched.length === 0) return;

    scored.forEach((entry) => {
      if (!entry.matchesAll) entry.card.remove();
    });

    if (terms.length < 2) return;

    matched
      .sort((a, b) => a.leftover - b.leftover || a.index - b.index)
      .forEach((entry) => list.appendChild(entry.card));
  }

  /**
   * Runs on the parsed response before it is injected. Filters the product grid,
   * then trims every list back to displayLimit — we ask the endpoint for more than
   * we show (see fetchLimit), so without this the dropdown would render the surplus.
   */
  applyResultLimits(contentElement) {
    if (this.strictMatch) this.filterAndRankProducts(contentElement);

    const lists = [
      ['#predictive-search-results-products-list', '.product-column'],
      ['#predictive-search-results-queries-list', 'li'],
      ['#predictive-search-results-collection-list', 'li'],
      ['#predictive-search-results-article-page-list', 'li'],
    ];

    lists.forEach(([listSelector, itemSelector]) => {
      const list = contentElement.querySelector(listSelector);
      if (!list) return;
      Array.from(list.querySelectorAll(itemSelector))
        .slice(this.displayLimit)
        .forEach((item) => item.remove());
    });
  }

  onFocus(event) {
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom)}px`
    );
    if (this.isHeaderSearch && !this.searchRecommendationEmpty) {
      document.body.classList.add('search-open');
      this.toggleNavButtonVisibility(false);
    }

    if (!this.searchRecommendationEmpty) {
      this.searchContent.classList.remove('hidden');
    }
    this.classList.add('predictive-search-open');
    if (this.getQuery().length === 0) {
      if (this.searchRecommendationEmpty) {
        this.searchContent.classList.add('hidden');
      }
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url, event);
  }

  getQuery() {
    return this.input.value.trim();
  }

  clear(event = null) {
    event?.preventDefault();
    this.input.value = '';
    this.input.focus();
    this.removeAttribute('results');
    this.toggleSearchState(false);
  }

  handleProductTypeChange(evt) {
    const query = this.getQuery();
    if (query.length > 0) {
      const url = this.setupURL().toString();
      this.renderSection(url);
    }
  }

  // Prefer the real browser origin (address bar). On `shopify theme dev`, that is 127.0.0.1:9292.
  // `window.shopUrl` / `request.origin` from Liquid can still be *.myshopify.com, which makes fetch
  // cross-origin and triggers CORS + broken redirects.
  buildURL(path) {
    const base =
      typeof window.location?.origin === 'string' &&
      window.location.origin &&
      window.location.origin !== 'null'
        ? window.location.origin
        : typeof window.shopUrl === 'string' && window.shopUrl.length > 0
          ? window.shopUrl
          : FoxTheme.routes.shop_url;
    return path.startsWith('http://') || path.startsWith('https://')
      ? new URL(path)
      : new URL(path, base);
  }

  /**
   * The product grid comes from the storefront search endpoint, not /search/suggest.
   * suggest caps resources[limit] at 10 and ranks on popularity, which for "iphone 16"
   * buries the base model beneath ten of its own variants so it never reaches the
   * dropdown; /search ranks the exact model first for the same query. The section
   * renders the two-column skeleton, and mergeResults() fills the right column from
   * the suggest response, which is still the only source of query suggestions.
   */
  setupProductsURL() {
    const url = this.buildURL(FoxTheme.routes.search_url || '/search');
    return (
      url.searchParams.set('q', this.getQuery()),
      url.searchParams.set('type', 'product'),
      url.searchParams.set('options[prefix]', 'last'),
      url.searchParams.set('section_id', 'predictive-search-products'),
      url
    );
  }

  setupURL() {
    const url = this.buildURL(FoxTheme.routes.predictive_search_url);
    let search_term = this.getQuery();
    if (this.searchProductTypes && this.searchProductTypes.value != '') {
      search_term = `product_type:${this.searchProductTypes.value} AND ${encodeURIComponent(search_term)}`;
    }
    return (
      url.searchParams.set('q', search_term),
      url.searchParams.set('resources[limit]', this.fetchLimit),
      url.searchParams.set('resources[limit_scope]', 'each'),
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this)),
      url
    );
  }

  onChange() {
    if (this.getQuery().length === 0) {
      this.clear();
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url);
  }

  renderSection(url) {
    this.cachedMap.has(url) ? this.renderSectionFromCache(url) : this.renderSectionFromFetch(url);
  }

  renderSectionFromCache(url) {
    const payload = this.cachedMap.get(url);
    this.renderSearchResults(payload), this.setAttribute('results', '');
  }

  renderSectionFromFetch(url) {
    this.setLoadingState(true);

    // Both requests go out together on the existing 300ms debounce: suggest for the
    // queries/collections column, /search for the product grid. A failed products
    // request degrades to suggest's own grid rather than breaking the dropdown.
    Promise.all([
      fetch(url).then((response) => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.text();
      }),
      fetch(this.setupProductsURL().toString())
        .then((response) => (response.ok ? response.text() : ''))
        .catch(() => ''),
    ])
      .then(([suggestText, productsText]) => {
        const payload = { suggestText, productsText };
        this.renderSearchResults(payload);
        this.cachedMap.set(url, payload);
      })
      .catch((error) => {
        console.error('Error fetching data: ', error);
        this.setAttribute('error', 'Failed to load data');
      })
      .finally(() => this.setLoadingState(false));
  }

  /**
   * Returns the node to render: the /search-sourced skeleton with the suggest
   * response's queries/collections column moved into it, or suggest's own output
   * untouched when /search returned no product grid.
   */
  mergeResults(suggestContent, productsText) {
    if (!productsText) return suggestContent;

    const productsBlock = new DOMParser()
      .parseFromString(productsText, 'text/html')
      .getElementById('PredictiveSearchProducts');
    if (!productsBlock?.querySelector('#predictive-search-results-products-list')) return suggestContent;

    const othersSource = suggestContent?.querySelector('.search__box-others');
    const othersTarget = productsBlock.querySelector('[data-search-others]');
    if (othersSource && othersTarget) othersTarget.innerHTML = othersSource.innerHTML;

    return productsBlock;
  }

  renderSearchResults(payload) {
    const { suggestText, productsText } = payload;
    const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    const targetElement = document.getElementById(id);

    if (targetElement) {
      const parser = new DOMParser();
      const parsedDoc = parser.parseFromString(suggestText, 'text/html');
      const contentElement = this.mergeResults(parsedDoc.getElementById(id), productsText);

      if (contentElement) {
        this.applyResultLimits(contentElement);
        this.searchContent?.classList.remove('hidden');
        targetElement.innerHTML = contentElement.innerHTML;

        if (this.isHeaderSearch && !document.body.classList.contains('search-open')) {
          document.body.classList.add('search-open');
          this.toggleNavButtonVisibility(true);
          this.classList.add('predictive-search-open');
        } else {
          this.classList.add('predictive-search-open');
        }
      } else {
        console.error(`Element with id '${id}' not found in the parsed response.`);
      }
    } else {
      console.error(`Element with id '${id}' not found in the document.`);
    }
  }

  handleClickOutside(event) {
    const target = event.target;
    const shouldClose = this.isHeaderSearch
      ? !this.contains(target) &&
        ((target.classList.contains('fixed-overlay') && target.closest('.header-section')) ||
          target.classList.contains('header__search-close'))
      : !this.contains(target);

    if (shouldClose) {
      setTimeout(() => this.toggleSearchState(false));
    }
  }

  toggleSearchState(isOpen) {
    this.classList.toggle(this.states.OPEN, isOpen);
    if (this.isHeaderSearch) {
      document.body.classList.toggle(this.states.SEARCH_OPEN, isOpen);
      this.toggleNavButtonVisibility(!isOpen);
    }
    if (!isOpen && this.searchRecommendationEmpty) {
      this.searchContent?.classList.add('hidden');
    }
  }

  toggleNavButtonVisibility(isOpen) {
    this.toggleNavigationButton && this.toggleNavigationButton.classList.toggle('is-show', isOpen);
  }

  setLoadingState(isLoading) {
    if (isLoading) {
      this.setAttribute('loading', 'true');
      this.resetButton.classList.add(this.states.LOADING);
    } else {
      this.removeAttribute('loading');
      this.resetButton.classList.remove(this.states.LOADING);
      this.setAttribute('results', 'true');
    }
  }
}
customElements.define('predictive-search', PredictiveSearch);
